import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import { Field, FormikProvider, useFormik } from 'formik'
import CustomInput from '../../../components/common/CustomInput'
import CustomTextarea from '../../../components/common/CustomTextarea'
import CustomSelect from '../../../components/common/CustomSelect'
import CustomCheckBox from '../../../components/common/CustomCheckBox'
import * as Yup from 'yup'

const validationSchema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
  description: Yup.string().required('Description is required'),
  priority: Yup.string().trim().required('Priority is required'),
  dueDate: Yup.string().trim().required('Due Date is required'),
  assignTo: Yup.string().trim().required('Assign To is required'),
  company: Yup.string().trim().required('Company is required'),
  bigButton: Yup.boolean(),
  bigTaskType: Yup.mixed().when('bigButton', {
    is: true,
    then: (schema) => schema.required('Big Task Type is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  bigTaskStatus: Yup.mixed().when('bigButton', {
    is: true,
    then: (schema) => schema.required('Big Task Status is required'),
    otherwise: (schema) => schema.nullable(),
  }),
})

const TaskAdd = () => {
  const navigate = useNavigate()
  const [submitted, setSubmitted] = useState(false)
  const [TeamOptions, setTeamOptions] = useState([])
  const [companyOptions, setCompanyOptions] = useState([])
  const [showBigTaskModal, setShowBigTaskModal] = useState(false)
  const [bigTaskTypeOptions, setbigTaskTypeOptions] = useState([]);
  const [bigTaskStatusOptions, setbigTaskStatusOptions] = useState([]);


  const fetchTeamData = async () => {
    try {
      const response = await axiosAuthInstance.get('team/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.full_name} (${item.role})`,
        }))
        setTeamOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }

  const fetchCompanyData = async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: `${item.name}`,
        }))
        setCompanyOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }
  const fetchBigTaskTypeData = async () => {
    try {
      const response = await axiosAuthInstance.get('big-task-type/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setbigTaskTypeOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }
  const fetchBigTaskStatus = async () => {
    try {
      const response = await axiosAuthInstance.get('big-task-status/dropdown')
      if (response.status === 200) {
        const transformedData = response?.data?.result?.map((item) => ({
          value: item._id,
          label: item.name,
        }))
        setbigTaskStatusOptions(transformedData)
      }
    } catch (error) {
      console.log('error', error)
    }
  }
  useEffect(() => {
    fetchTeamData()
    fetchCompanyData()
    fetchBigTaskTypeData()
    fetchBigTaskStatus()
  }, [])

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitted(true)
    try {
      const response = await axiosAuthInstance.post('task/add', values)
      if (response.status === 200) {
        resetForm();
        navigate('/task')
      }
    } catch (error) {
      console.log(error)
    } finally {
      setSubmitted(false)
    }
  }

  const formik = useFormik({
    onSubmit: handleSubmit,
    validationSchema,
    initialValues: {
      company: '',
      name: '',
      description: '',
      priority: '',
      dueDate: '',
      assignTo: '',
      paidButton: false,
      bigButton: false,
      printButton: false,
      bigTaskType: null,
      bigTaskStatus: null,
    },
  })
  useEffect(() => {
    if (formik.values.bigButton) {
      setShowBigTaskModal(true)
    } else {
      formik.setFieldValue('bigTaskType', '')
      formik.setFieldValue('bigTaskStatus', '')
      setShowBigTaskModal(false)
    }
  }, [formik.values.bigButton])

  const PriorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' },
  ]

  return (
    <FormikProvider value={formik}>
      <form
        className='flex flex-col p-4 gap-4'
        onSubmit={(e) => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        <Field
          name='name'
          label='Task Name'
          placeholder='Task Name'
          required
          component={CustomInput}
        />
        <Field
          name='description'
          label='Description'
          placeholder='Description'
          required
          component={CustomTextarea}
        />

        <Field
          name='company'
          label='Company'
          placeholder='Company'
          required
          component={CustomSelect}
          options={companyOptions}
        />

        <Field
          name='priority'
          label='Priority'
          placeholder='Priority'
          required
          component={CustomSelect}
          options={PriorityOptions}
        />

        <Field
          name='dueDate'
          label='Due Date'
          placeholder='Due Date'
          required
          type='date'
          min={new Date().toISOString().split('T')[0]}
          component={CustomInput}
        />

        <Field
          name='assignTo'
          label='Assign To'
          placeholder='Assign To'
          required
          component={CustomSelect}
          options={TeamOptions}
        />

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <Field
            name='paidButton'
            label='Paid Task'
            component={CustomCheckBox}
          />
          <Field name='bigButton' label='Big Task' component={CustomCheckBox} onChange={(e) => {
            const isChecked = e.target.checked
            formik.setFieldValue('bigButton', isChecked)
            if (!isChecked) {
              formik.setFieldValue('bigTaskType', null)
              formik.setFieldValue('bigTaskStatus', null)
              // Mark fields as touched so errors appear
              formik.setFieldTouched('bigTaskType', true)
              formik.setFieldTouched('bigTaskStatus', true)
            }
          }} />
          <Field
            name='printButton'
            label='Printing Task'
            component={CustomCheckBox}

          />
        </div>

        {showBigTaskModal && (
          <div className='bg-white border border-gray-300 rounded-lg p-4 mt-4 shadow'>
            <h2 className='text-lg font-semibold mb-4'>Big Task Details</h2>

            <CustomSelect
              field={{
                name: 'bigTaskType',
                value: formik.values.bigTaskType,
                onChange: formik.handleChange,
              }}
              form={formik}
              label='Big Task Type'
              options={bigTaskTypeOptions}
              required
            />

            <div className='mt-4'>
              <CustomSelect
                field={{
                  name: 'bigTaskStatus',
                  value: formik.values.bigTaskStatus,
                  onChange: formik.handleChange,
                }}
                form={formik}
                label='Big Task Status'
                options={bigTaskStatusOptions}
                required
              />
            </div>
          </div>
        )}

        <button
          type='submit'
          disabled={submitted}
          className='bg-primary text-xl font-medium text-white rounded-lg px-6 py-2'
        >
          {submitted ? (
            <div className='w-5 h-5 mx-auto border-2 border-white border-solid rounded-full loader animate-spin border-t-transparent'></div>
          ) : (
            'Create Task'
          )}
        </button>
      </form>
    </FormikProvider>
  )
}

export default TaskAdd